import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  TrendingUp,
  Sparkles,
  Coins,
  Bot,
  FileText,
  Activity,
  CircleDot,
  Briefcase,
  Bell,
  Star,
  ShieldCheck,
  Mail,
  Settings,
  FolderSearch,
  Layers,
  GitCompare,
  BookOpen,
  Newspaper,
  Beaker,
  MessageSquare,
  Rocket,
  BarChart3,
  Globe2,
  ShieldAlert,
} from "lucide-react";
import { apiGet, formatDateTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/market-intelligence", label: "Market Intelligence", icon: Newspaper, testId: "nav-market-intelligence" },
  { to: "/ipo", label: "IPO Analysis", icon: Rocket, testId: "nav-ipo" },
  { to: "/stocks", label: "Stocks", icon: TrendingUp, testId: "nav-stocks" },
  { to: "/us-stocks", label: "US Stocks", icon: Globe2, testId: "nav-us-stocks", flag: "us_stocks_enabled" },
  { to: "/sectors", label: "Sectors", icon: Layers, testId: "nav-sectors" },
  { to: "/watchlist", label: "Watchlist", icon: Star, testId: "nav-watchlist" },
  { to: "/portfolio", label: "My Portfolio", icon: Briefcase, testId: "nav-portfolio" },
  { to: "/best-value", label: "Best Value Finder", icon: Sparkles, testId: "nav-best-value" },
  { to: "/track-record", label: "Track Record", icon: BarChart3, testId: "nav-track-record" },
  { to: "/compare", label: "Compare", icon: GitCompare, testId: "nav-compare" },
  { to: "/dividends", label: "Dividends", icon: Coins, testId: "nav-dividends" },
  { to: "/ai-insights", label: "AI Insights", icon: Bot, testId: "nav-ai-insights" },
  { to: "/backtest", label: "Backtesting", icon: Beaker, testId: "nav-backtest" },
  { to: "/alerts", label: "Alerts", icon: Bell, testId: "nav-alerts" },
  // BETA MODE: regular users access company reports via the Stock Detail
  // page → Reports tab. The standalone /reports page and /pricing page
  // are kept reachable by URL (route exists) but are no longer linked.
  { to: "/glossary", label: "Glossary", icon: BookOpen, testId: "nav-glossary" },
];

const ADMIN_NAV_ITEMS = [
  { to: "/system-status", label: "System Status", icon: Activity, testId: "nav-system-status" },
  { to: "/admin/api-costs", label: "API Costs", icon: Coins, testId: "nav-admin-api-costs" },
  { to: "/admin/users", label: "User Management", icon: ShieldCheck, testId: "nav-admin-users" },
  { to: "/reports", label: "Reports", icon: FileText, testId: "nav-reports" },
  { to: "/admin/reports", label: "Report Monitor", icon: FolderSearch, testId: "nav-admin-reports" },
  { to: "/admin/report-pipeline", label: "Report Pipeline", icon: Activity, testId: "nav-admin-report-pipeline" },
  { to: "/admin/market-intelligence", label: "Data Sources", icon: Newspaper, testId: "nav-admin-data-sources" },
  { to: "/admin/email-logs", label: "Email Logs", icon: Mail, testId: "nav-admin-email-logs" },
  { to: "/admin/feedback", label: "User Feedback", icon: MessageSquare, testId: "nav-admin-feedback" },
  { to: "/admin/us-universe", label: "US Universe", icon: Globe2, testId: "nav-admin-us-universe" },
  { to: "/admin/orb", label: "Admin ORB", icon: Activity, testId: "nav-admin-orb" },
  { to: "/oracle-trades", label: "Oracle Trades", icon: Activity, testId: "nav-oracle-trades" },
  { to: "/admin/data-quality", label: "Data Quality", icon: ShieldAlert, testId: "nav-admin-data-quality" },
  { to: "/settings", label: "Settings", icon: Settings, testId: "nav-settings" },
];

const SidebarNav = ({ onNavigate }) => {
  const [health, setHealth] = useState(null);
  const { isAdmin, user } = useAuth();

  useEffect(() => {
    apiGet("/health").then(setHealth).catch(() => {});
    const interval = setInterval(() => {
      apiGet("/health").then(setHealth).catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Filter nav items by feature flags. Admins always see everything;
  // non-admins only see flag-gated items when their user doc has the
  // matching boolean flag set.
  const visibleNavItems = NAV_ITEMS.filter((item) => {
    if (!item.flag) return true;
    if (isAdmin) return true;
    return !!(user && user[item.flag]);
  });

  return (
    <div className="flex h-full flex-col">
      <div className="px-5 py-5 border-b border-border">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center font-display font-bold">
            CI
          </div>
          <div>
            <div className="font-display font-semibold text-base leading-none">CSE Investor</div>
            <div className="text-xs text-muted-foreground mt-1">Colombo Stock Exchange</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto py-3 px-3 space-y-1" data-testid="sidebar-nav-scroll">
        {visibleNavItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                  isActive
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          );
        })}

        {isAdmin && (
          <>
            <div className="pt-4 pb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              Administration
            </div>
            {ADMIN_NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onNavigate}
                  data-testid={item.testId}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                      isActive
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              );
            })}
          </>
        )}
      </nav>

      <div className="px-4 py-4 border-t border-border text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <CircleDot
            className={`h-3 w-3 ${
              health?.scheduler?.running ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]"
            }`}
          />
          <span>{health?.scheduler?.running ? "Scheduler running" : "Scheduler stopped"}</span>
        </div>
        <div className="mt-1.5">Updated {formatDateTime(health?.timestamp)}</div>
      </div>
    </div>
  );
};

export default SidebarNav;
